// #145 — Preserva edições não salvas do NCEditor contra perda SILENCIOSA
// quando a sessão expira de vez (refresh token vencido/revogado → logout
// na marra com window.location.href).
//
// Fluxo:
//   1. O NCEditor chama recordDraft() a cada mudança → guarda o rascunho EM
//      MEMÓRIA (barato, sem I/O).
//   2. Os interceptors de auth (api.js / fetchInterceptor.js), no instante
//      EXATO em que vão deslogar à força, chamam flushDraftToStorage() →
//      persiste o rascunho no localStorage + marca a flag de sessão expirada.
//   3. Após o re-login, ao reabrir a NC, o NCEditor acha o rascunho via
//      peekPersistedDraft() e oferece restaurar.
//
// Por que flush só na queda (e não autosave contínuo)? Assim o "restaurar?"
// só aparece quando houve perda REAL de sessão — zero falso positivo em
// reload/navegação normal (esses limpam o rascunho de memória ao desmontar).

const STORAGE_KEY = 'qaasa_nc_draft';
const SESSION_EXPIRED_FLAG = 'qaasa_session_expired';

let _memDraft = null; // { ncId, editedData, ts }

// NCEditor: registra o rascunho atual (em memória).
export function recordDraft(ncId, editedData) {
  if (ncId == null) return;
  _memDraft = { ncId, editedData, ts: Date.now() };
}

// NCEditor: limpa o rascunho de memória (não há mais pendência).
export function clearDraft() {
  _memDraft = null;
}

// Interceptors de auth: persiste o rascunho ANTES do logout/redirect.
// Síncrono — completa antes do window.location.href. Marca a flag pra
// tela de login avisar o usuário.
export function flushDraftToStorage() {
  try {
    if (_memDraft) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(_memDraft));
    }
    // #215 — o FORMULÁRIO de criação de NC também é preservado na queda
    // (mesma mecânica: memória barata + flush só no logout forçado).
    if (_memFormDraft) {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(_memFormDraft));
    }
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch (_) {
    /* storage indisponível — best-effort, não quebra o logout */
  }
}

// ─── #215 — rascunho do FORMULÁRIO de criação de NC (espelha o do editor) ───
// Arquivos anexados NÃO são preserváveis (File não serializa) — o restore
// repõe os campos de texto e avisa pra re-anexar.

const FORM_STORAGE_KEY = 'qaasa_nc_form_draft';

let _memFormDraft = null; // { payload: {formData, modoAnalise, origemCustom, industriaCustom}, ts }

// NCForm: registra o estado atual do formulário (em memória).
export function recordFormDraft(payload) {
  _memFormDraft = { payload, ts: Date.now() };
}

// NCForm: limpa memória + persistido (submit ok, descarte ou desmonte limpo).
export function clearFormDraft() {
  _memFormDraft = null;
  try {
    localStorage.removeItem(FORM_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

// NCForm (mount): existe formulário preservado de uma queda de sessão?
export function peekPersistedFormDraft() {
  try {
    const raw = localStorage.getItem(FORM_STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && d.payload && d.payload.formData) return d;
  } catch (_) {
    /* JSON corrompido/storage indisponível — ignora */
  }
  return null;
}

// NCEditor (mount): existe rascunho preservado para ESTA NC?
export function peekPersistedDraft(ncId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && d.editedData && String(d.ncId) === String(ncId)) return d;
  } catch (_) {
    /* JSON corrompido/storage indisponível — ignora */
  }
  return null;
}

// NCEditor: remove o rascunho persistido (após restaurar ou descartar, ou
// após um save bem-sucedido que o torna obsoleto).
export function discardPersistedDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

// Login: consome (lê + limpa) a flag de sessão expirada — pra mostrar o
// aviso uma única vez.
export function consumeSessionExpiredFlag() {
  try {
    const v = sessionStorage.getItem(SESSION_EXPIRED_FLAG);
    if (v) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}
